import {
	DetectedPdfField,
	PdfFieldOption,
	PdfFieldRect,
	PdfFieldType,
} from '@/interfaces';

const LABEL_SEARCH_RADIUS = 180;
const ALIGNMENT_TOLERANCE = 6;

const mapFieldType = (annotation: any): PdfFieldType => {
	switch (annotation.fieldType) {
		case 'Tx':
			return 'text';
		case 'Sig':
			return 'signature';
		case 'Ch':
			return annotation.combo ? 'dropdown' : 'listbox';
		case 'Btn':
			if (annotation.radioButton) return 'radio';
			if (annotation.checkBox) return 'checkbox';
			return 'button';
		default:
			return 'unknown';
	}
};

const optionsFromAnnotation = (
	annotation: any
): PdfFieldOption[] | undefined => {
	const raw = annotation.options ?? annotation.data?.options;
	if (!Array.isArray(raw) || raw.length === 0) return undefined;

	const options = raw
		.map((option: any) => {
			const value = String(option?.exportValue ?? option?.displayValue ?? '');
			const optionLabel = String(
				option?.displayValue ?? option?.exportValue ?? ''
			);
			return { label: optionLabel, value };
		})
		.filter((option: PdfFieldOption) => option.value || option.label);

	return options.length > 0 ? options : undefined;
};

const defaultValueFromAnnotation = (
	annotation: any,
	fieldType: PdfFieldType
): string | undefined => {
	const raw = annotation.fieldValue;
	const normalized = Array.isArray(raw)
		? String(raw[0] ?? '')
		: raw == null
		? ''
		: String(raw);

	if (fieldType === 'radio') {
		const exportValue = String(
			annotation.buttonValue ?? annotation.exportValue ?? ''
		);
		if (exportValue && normalized === exportValue) return exportValue;
		return undefined;
	}

	if (fieldType === 'dropdown' || fieldType === 'listbox') {
		return normalized.trim() ? normalized : undefined;
	}

	return undefined;
};

const hasFilledValue = (annotation: any, fieldType: PdfFieldType): boolean => {
	const raw = annotation.fieldValue;
	const normalized = Array.isArray(raw)
		? raw.filter(Boolean).join('').trim()
		: raw == null
		? ''
		: String(raw).trim();

	if (!normalized) return false;

	if (fieldType === 'checkbox') return normalized !== 'Off';

	if (fieldType === 'dropdown' || fieldType === 'listbox') {
		const firstOption = annotation.options?.[0];
		const defaultValues = [
			firstOption?.exportValue,
			firstOption?.displayValue,
		].map((value) => String(value ?? '').trim());
		return !defaultValues.includes(normalized);
	}

	if (fieldType === 'radio') {
		const exportValue = String(
			annotation.buttonValue ?? annotation.exportValue ?? ''
		);
		return !!exportValue && normalized === exportValue;
	}

	return true;
};

export const isConfigurablePdfField = (field: DetectedPdfField): boolean =>
	field.fieldType !== 'checkbox' && field.fieldType !== 'radio';

const rectFromAnnotation = (rect: number[]): PdfFieldRect => {
	const [x1, y1, x2, y2] = rect;
	return {
		x: Math.min(x1, x2),
		y: Math.min(y1, y2),
		width: Math.abs(x2 - x1),
		height: Math.abs(y2 - y1),
	};
};

interface TextItemBounds {
	str: string;
	left: number;
	right: number;
	bottom: number;
	top: number;
	centerX: number;
	centerY: number;
}

const boundsFromTextItem = (item: any): TextItemBounds | null => {
	const str: string = (item.str ?? '').trim();
	if (!str) return null;

	const [, , , , x, y] = item.transform;
	const width: number = item.width ?? 0;
	const height: number = item.height ?? 0;

	return {
		str,
		left: x,
		right: x + width,
		bottom: y,
		top: y + height,
		centerX: x + width / 2,
		centerY: y + height / 2,
	};
};

interface LabelCandidate {
	bounds: TextItemBounds;
	distance: number;
}

interface LabelCandidates {
	left: LabelCandidate | null;
	above: LabelCandidate | null;
	fallback: LabelCandidate | null;
}

const pickClosest = (
	current: LabelCandidate | null,
	bounds: TextItemBounds,
	distance: number
): LabelCandidate | null => {
	if (distance > LABEL_SEARCH_RADIUS) return current;
	if (!current || distance < current.distance) return { bounds, distance };
	return current;
};

const findNearestLabel = (
	rect: PdfFieldRect,
	textItems: any[]
): string | null => {
	const rectTop = rect.y + rect.height;
	const bounds = textItems
		.map(boundsFromTextItem)
		.filter((item): item is TextItemBounds => item !== null);

	const candidates = bounds.reduce<LabelCandidates>(
		(acc, item) => {
			const verticallyAligned =
				item.top >= rect.y - ALIGNMENT_TOLERANCE &&
				item.bottom <= rectTop + ALIGNMENT_TOLERANCE;
			const horizontallyAligned =
				item.left >= rect.x - ALIGNMENT_TOLERANCE &&
				item.right <= rect.x + rect.width + ALIGNMENT_TOLERANCE;

			const left =
				item.right <= rect.x + ALIGNMENT_TOLERANCE && verticallyAligned
					? pickClosest(acc.left, item, rect.x - item.right)
					: acc.left;

			const above =
				item.bottom >= rectTop - ALIGNMENT_TOLERANCE && horizontallyAligned
					? pickClosest(acc.above, item, item.bottom - rectTop)
					: acc.above;

			const dx = item.centerX - (rect.x + rect.width / 2);
			const dy = item.centerY - (rect.y + rect.height / 2);
			const fallback = pickClosest(
				acc.fallback,
				item,
				Math.sqrt(dx * dx + dy * dy)
			);

			return { left, above, fallback };
		},
		{ left: null, above: null, fallback: null }
	);

	const winner = candidates.left ?? candidates.above ?? candidates.fallback;
	return winner ? winner.bounds.str : null;
};

export const detectPdfFields = async (
	pdfDocument: any
): Promise<DetectedPdfField[]> => {
	const detectedFields: DetectedPdfField[] = [];

	for (let pageIndex = 0; pageIndex < pdfDocument.numPages; pageIndex += 1) {
		// eslint-disable-next-line no-await-in-loop
		const page = await pdfDocument.getPage(pageIndex + 1);
		// eslint-disable-next-line no-await-in-loop
		const [annotations, textContent] = await Promise.all([
			page.getAnnotations({ intent: 'display' }),
			page.getTextContent(),
		]);

		const widgetAnnotations = (annotations ?? []).filter(
			(annotation: any) =>
				annotation.subtype === 'Widget' && !!annotation.fieldType
		);

		widgetAnnotations.forEach((annotation: any) => {
			const fieldType = mapFieldType(annotation);
			if (hasFilledValue(annotation, fieldType)) return;

			const rect = rectFromAnnotation(annotation.rect);
			const label =
				findNearestLabel(rect, textContent?.items ?? []) ??
				annotation.fieldName ??
				annotation.id;

			detectedFields.push({
				id: `${pageIndex}-${annotation.id}`,
				fieldName: annotation.fieldName ?? annotation.id,
				label,
				fieldType,
				pageIndex,
				rect,
				options: optionsFromAnnotation(annotation),
				defaultValue: defaultValueFromAnnotation(annotation, fieldType),
				exportValue:
					fieldType === 'radio'
						? String(annotation.buttonValue ?? annotation.exportValue ?? '') ||
						  undefined
						: undefined,
			});
		});
	}

	return detectedFields;
};
